import React, { useEffect, useState } from 'react';

import { Box, Flex, Heading, Text, VStack } from '@chakra-ui/react';
import TwoArrowsLeft from 'images/doodles/two-arrows-left.png';
import Image from 'next/image';
import Link from 'next/link';

import { InventoryDefinitionArticle } from '@/components/sections/inventory/typings';
import { BlogPath, getDynamicPath } from '@/utils/models';

type InventoryDefinitionReadMoreProps = {
  articles: InventoryDefinitionArticle[];
};

const DEFAULT_ANIMATION_INTERVAL = 4000;
const DEFAULT_ANIMATION_TIME = '1s';

export const InventoryDefinitionReadMore = ({ articles }: InventoryDefinitionReadMoreProps) => {
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    const animate = () => {
      setShouldAnimate(true);
      setTimeout(() => {
        setShouldAnimate(false);
      }, 1000); // Reset animation after 1 second
    };

    const intervalId = setInterval(animate, DEFAULT_ANIMATION_INTERVAL); // Trigger the animation every 4 seconds

    return () => {
      clearInterval(intervalId); // Clear the interval on component unmount
    };
  }, []);

  return (
    <Flex
      mt={{
        base: 0,
        xs: 6,
        lg: 16,
      }}
      position="relative"
      flexDir={{
        base: 'column',
        xs: 'row',
        lg: 'column',
      }}
      alignItems={{
        base: 'start',
        xs: 'flex-end',
        lg: 'start',
      }}
      gap={{
        base: 4,
        lg: 0,
      }}
    >
      <Box
        as={Image}
        src={TwoArrowsLeft}
        alt=""
        w="93px"
        ml={{
          base: 2,
          xs: 0,
        }}
        mb={{
          base: -4,
          xs: 0,
        }}
        position={{
          base: 'initial',
          lg: 'absolute',
        }}
        transform={{
          base: 'rotate(90deg)',
          xs: 'none',
        }}
        bottom={0}
        left="-100px"
        sx={{
          '@keyframes zoom': {
            '0%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.2)' },
            '100%': { transform: 'scale(1)' },
          },
          animation: shouldAnimate ? `zoom ${DEFAULT_ANIMATION_TIME} alternate ease-in-out` : 'none',
        }}
      />
      <Box>
        <Heading
          as="h2"
          color="purple.500"
          variant={{
            base: 'component-paragraph-headline/2xl',
            md: 'component-paragraph-headline/3xl',
          }}
          marginBottom={5}
        >
          Read more on our blog
        </Heading>
        <VStack align="flex-start">
          {articles.map((article) => {
            if (article) {
              return (
                <Link key={article.slug} href={getDynamicPath(BlogPath.ARTICLE, article.slug)}>
                  <Text
                    color="violet.500"
                    variant={{
                      base: 'text-md/lineHeight-6/font-semibold',
                    }}
                    _hover={{
                      textDecoration: 'underline',
                    }}
                  >
                    {article?.title}
                  </Text>
                </Link>
              );
            }
          })}
        </VStack>
      </Box>
    </Flex>
  );
};
